import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Button, Stack } from '@munifocus/ui';
import type { SafetyEventView } from '@munifocus/contracts-gov';
import { useAuth } from '../../providers/AuthProvider';
import { useOptionalScope } from '../../providers/ScopeProvider';
import {
  bannerAlerts,
  safetyKeys,
  shortTime,
  useSafetyBoard,
  useSafetyStatus,
} from '../../lib/safety-api';

/**
 * The supervisor's "somebody needs you" strip, above every screen.
 *
 * Fed by two sources so it is right whichever arrives first: alerts pushed to
 * this user over the socket (any municipality they answer for), and the board
 * of the municipality on screen, which polls. Shows only alerts nobody has
 * acknowledged yet, and never the viewer's own alarm. Hidden on the board
 * itself, which already shows them in full.
 */
export function SafetyAlertBanner() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const scope = useOptionalScope();
  const status = useSafetyStatus(!!user);
  const lgId = scope?.active?.kind === 'local_government' ? scope.active.id : undefined;
  const responds = !!lgId && !!status.data?.respondsAt.includes(lgId);
  const board = useSafetyBoard(responds ? lgId : undefined);
  const pushed = useQuery<SafetyEventView[]>({
    queryKey: safetyKeys.pushed,
    queryFn: () => [],
    enabled: false,
    initialData: [],
  });

  if (!status.data?.respondsAt.length || pathname.startsWith('/safety')) return null;
  const alerts = bannerAlerts([pushed.data, board.data?.events], user?.id);
  if (!alerts.length) return null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {alerts.slice(0, 3).map((a) => {
        const where = [a.fileReference, a.address].filter(Boolean).join(' — ');
        return (
          <Alert
            key={a.id}
            severity="error"
            role="alert"
            action={
              <Button component={RouterLink} to="/safety" color="inherit" size="small">
                {t('gov.safety.banner.view')}
              </Button>
            }
          >
            <AlertTitle sx={{ mb: 0 }}>
              {t(a.kind === 'sos' ? 'gov.safety.banner.sos' : 'gov.safety.banner.overdue', {
                name: a.officerName,
              })}
            </AlertTitle>
            {where ? `${t('gov.safety.banner.at', { where })} · ` : ''}
            {t('gov.safety.board.raisedAt', { time: shortTime(a.raisedAt, i18n.language) })}
            {a.escalationLevel >= 2 ? ` · ${t('gov.safety.board.escalated')}` : ''}
          </Alert>
        );
      })}
    </Stack>
  );
}
